import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  createOrGetConversation,
  fetchConversationMessages,
  markConversationRead,
  sendConversationMessage,
  type MessagingConversation,
  type MessagingMessage,
} from '../api/messagingApi';
import { AssetRequestError } from '../api/getAssetById';
import { normalizeStoryUserId } from '../api/socialStoriesApi';
import { getSessionToken } from '../auth/sessionToken';
import { extractSessionOwnerIdFromAccessToken } from '../utils/assetOwnerMatch';
import { useSocialPosts } from '../hooks/useSocialPosts';
import { useMessagingInbox } from '../messaging/MessagingInboxContext';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import type { TranslateParams } from '../i18n/translate';
import { ProfileAvatarImage } from '../components/ProfileAvatarImage';
import pageThemeStyles from '../components/menuPageTheme.module.css';
import {
  buildStoryInitialsAvatarSrc,
  resolveStoryGroupAvatarSrc,
} from '../utils/profileAvatarDisplay';
import {
  formatMessageTime,
  resolveMessagingPeerDisplay,
} from '../utils/messagingPeerDisplay';
import { rememberMessagingPeerDisplay } from '../utils/messagingPeerCache';
import { resolveChatTextDir } from '../utils/textDirection';
import type { SocialPostUserGroup } from '../utils/socialPostsStorage';
import styles from './MessagesPage.module.css';

const THREAD_POLL_MS = 4000;

type ComposeContact = {
  userId: string;
  label: string;
  avatarSrc: string;
};

function InboxConversationRow({
  conversation,
  groups,
  peerFallback,
  language,
  t,
  onOpen,
}: {
  conversation: MessagingConversation;
  groups: readonly SocialPostUserGroup[];
  peerFallback: string;
  language: string;
  t: (key: MessageKey, params?: TranslateParams) => string;
  onOpen: () => void;
}) {
  const peer = resolveMessagingPeerDisplay(conversation.peerUserId, groups, peerFallback, {
    peerLabel: conversation.peerLabel,
    peerAvatarSrc: conversation.peerAvatarSrc,
  });
  const preview = conversation.lastMessage?.text?.trim() || t('messages.noMessagesYet');
  const unread = conversation.unreadCount > 0;
  return (
    <button
      type="button"
      className={[styles.inboxRow, unread ? styles.inboxRowUnread : ''].filter(Boolean).join(' ')}
      onClick={onOpen}
    >
      <span className={unread ? styles.inboxAvatarUnread : undefined}>
        <ProfileAvatarImage src={peer.avatarSrc} className={styles.inboxAvatar} />
      </span>
      <span className={styles.inboxBody}>
        <span className={styles.inboxTop}>
          <span className={[styles.inboxName, unread ? styles.inboxNameUnread : ''].filter(Boolean).join(' ')}>
            {peer.label}
          </span>
          <span className={[styles.inboxTime, unread ? styles.inboxTimeUnread : ''].filter(Boolean).join(' ')}>
            {formatMessageTime(
              conversation.lastMessage?.createdAt ?? conversation.updatedAt,
              language,
            )}
          </span>
        </span>
        <span
          className={[styles.inboxPreview, unread ? styles.inboxPreviewUnread : '']
            .filter(Boolean)
            .join(' ')}
          dir={resolveChatTextDir(preview, language)}
        >
          {preview}
        </span>
      </span>
      {unread ? (
        <span
          className={styles.unreadBadge}
          aria-label={t('messages.unreadCount', {
            count: String(conversation.unreadCount),
          })}
        >
          {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
        </span>
      ) : null}
    </button>
  );
}

type MessagesPageProps = {
  tabFocus: number;
  pendingPeerUserId?: string | null;
  openCompose?: boolean;
  onPendingPeerConsumed?: () => void;
  onComposeConsumed?: () => void;
};

export function MessagesPage({
  tabFocus,
  pendingPeerUserId = null,
  openCompose = false,
  onPendingPeerConsumed,
  onComposeConsumed,
}: MessagesPageProps) {
  const { t, language } = useI18n();
  const { groups } = useSocialPosts();
  const {
    conversations,
    loading: loadingInbox,
    error: inboxErrorRaw,
    refresh: refreshInbox,
    setConversations,
    markConversationUnreadZero,
  } = useMessagingInbox();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const currentUserId =
    extractSessionOwnerIdFromAccessToken(getSessionToken())?.trim() || '';

  const inboxError =
    inboxErrorRaw === 'messaging-inbox-failed' ? t('messages.loadFailed') : inboxErrorRaw;
  const [active, setActive] = useState<MessagingConversation | null>(null);
  const [messages, setMessages] = useState<MessagingMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composePeerId, setComposePeerId] = useState<string | null>(null);
  const [composeText, setComposeText] = useState('');
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeFilter, setComposeFilter] = useState('');
  const [openPeerError, setOpenPeerError] = useState<string | null>(null);

  const peerFallback = t('messages.unknownPeer');

  const contacts = useMemo(() => {
    const map = new Map<string, ComposeContact>();
    const self = normalizeStoryUserId(currentUserId);
    for (const group of groups) {
      const id = group.userId.trim();
      if (!id || normalizeStoryUserId(id) === self) {
        continue;
      }
      const key = normalizeStoryUserId(id);
      if (map.has(key)) {
        continue;
      }
      const label = group.userLabel.trim() || peerFallback;
      map.set(key, {
        userId: id,
        label,
        avatarSrc: resolveStoryGroupAvatarSrc(group) ?? buildStoryInitialsAvatarSrc(label),
      });
      rememberMessagingPeerDisplay(id, {
        label,
        avatarSrc: resolveStoryGroupAvatarSrc(group) ?? buildStoryInitialsAvatarSrc(label),
      });
    }
    for (const conversation of conversations) {
      const id = conversation.peerUserId.trim();
      if (!id) {
        continue;
      }
      const key = normalizeStoryUserId(id);
      if (map.has(key)) {
        continue;
      }
      const display = resolveMessagingPeerDisplay(id, groups, peerFallback, {
        peerLabel: conversation.peerLabel,
        peerAvatarSrc: conversation.peerAvatarSrc,
      });
      map.set(key, {
        userId: id,
        label: display.label,
        avatarSrc: display.avatarSrc,
      });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, language));
  }, [groups, conversations, currentUserId, peerFallback, language]);

  const filteredContacts = useMemo(() => {
    const q = composeFilter.trim().toLowerCase();
    if (!q) {
      return contacts;
    }
    return contacts.filter(
      (contact) =>
        contact.label.toLowerCase().includes(q) || contact.userId.toLowerCase().includes(q),
    );
  }, [contacts, composeFilter]);

  const unreadConversations = useMemo(
    () => conversations.filter((row) => row.unreadCount > 0),
    [conversations],
  );
  const earlierConversations = useMemo(
    () => conversations.filter((row) => row.unreadCount <= 0),
    [conversations],
  );

  const loadThread = useCallback(
    async (conversationId: string, opts?: { soft?: boolean }) => {
      if (!opts?.soft) {
        setLoadingThread(true);
      }
      try {
        const rows = await fetchConversationMessages(conversationId, { limit: 50 });
        setMessages(rows);
        setThreadError(null);
        try {
          await markConversationRead(conversationId);
          markConversationUnreadZero(conversationId);
        } catch {
          /* ignore read failures */
        }
      } catch (err) {
        const message =
          err instanceof AssetRequestError
            ? err.message
            : t('messages.threadLoadFailed');
        setThreadError(message);
      } finally {
        if (!opts?.soft) {
          setLoadingThread(false);
        }
      }
    },
    [t, markConversationUnreadZero],
  );

  const startCompose = useCallback(() => {
    setActive(null);
    setMessages([]);
    setComposing(true);
    setComposePeerId(null);
    setComposeText('');
    setComposeError(null);
    setComposeFilter('');
  }, []);

  const closeCompose = useCallback(() => {
    setComposing(false);
    setComposePeerId(null);
    setComposeText('');
    setComposeError(null);
    setComposeFilter('');
  }, []);

  useEffect(() => {
    if (tabFocus < 1) {
      return;
    }
    panelRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    // Keep an in-flight peer deep-link; otherwise reset to inbox on tab re-select.
    if (!pendingPeerUserId?.trim()) {
      setActive(null);
      setDraft('');
      setComposing(false);
    }
  }, [tabFocus, pendingPeerUserId]);

  useEffect(() => {
    if (!openCompose) {
      return;
    }
    startCompose();
    onComposeConsumed?.();
  }, [openCompose, onComposeConsumed, startCompose]);

  useEffect(() => {
    setActive((current) => {
      if (!current) {
        return current;
      }
      return conversations.find((row) => row.id === current.id) ?? current;
    });
  }, [conversations]);

  useEffect(() => {
    if (tabFocus < 1) {
      return;
    }
    void refreshInbox({ soft: true });
  }, [tabFocus, refreshInbox]);

  useEffect(() => {
    const peer = pendingPeerUserId?.trim();
    if (!peer) {
      return;
    }
    let cancelled = false;
    setOpenPeerError(null);
    setComposing(false);
    void (async () => {
      try {
        const conversation = await createOrGetConversation(peer);
        if (cancelled) {
          return;
        }
        setComposing(false);
        setActive(conversation);
        setConversations((prev) => {
          if (prev.some((row) => row.id === conversation.id)) {
            return prev.map((row) => (row.id === conversation.id ? conversation : row));
          }
          return [conversation, ...prev];
        });
        await loadThread(conversation.id);
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof AssetRequestError
              ? err.message
              : t('messages.startFailed');
          setOpenPeerError(message);
        }
      } finally {
        onPendingPeerConsumed?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingPeerUserId, loadThread, onPendingPeerConsumed, setConversations, t]);

  useEffect(() => {
    if (!active?.id) {
      return;
    }
    void loadThread(active.id);
    const timer = window.setInterval(() => {
      void loadThread(active.id, { soft: true });
    }, THREAD_POLL_MS);
    return () => window.clearInterval(timer);
  }, [active?.id, loadThread]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, active?.id]);

  const onOpenThread = useCallback((conversation: MessagingConversation) => {
    setComposing(false);
    setActive(conversation);
    setDraft('');
    setThreadError(null);
  }, []);

  const onBackToInbox = useCallback(() => {
    setActive(null);
    setMessages([]);
    setDraft('');
    setThreadError(null);
    setComposing(false);
    void refreshInbox({ soft: true });
  }, [refreshInbox]);

  const onSend = useCallback(async () => {
    if (!active || sending) {
      return;
    }
    const text = draft.trim();
    if (!text) {
      return;
    }
    setSending(true);
    setThreadError(null);
    try {
      const sent = await sendConversationMessage(active.id, text);
      setDraft('');
      setMessages((prev) =>
        prev.some((row) => row.id === sent.id) ? prev : [...prev, sent],
      );
      setConversations((prev) => {
        const next = prev.map((row) =>
          row.id === active.id
            ? {
                ...row,
                lastMessage: sent,
                updatedAt: sent.createdAt,
                unreadCount: 0,
              }
            : row,
        );
        return next.sort((a, b) => b.updatedAt - a.updatedAt);
      });
    } catch (err) {
      const message =
        err instanceof AssetRequestError ? err.message : t('messages.sendFailed');
      setThreadError(message);
    } finally {
      setSending(false);
    }
  }, [active, draft, sending, t]);

  const onComposeSend = useCallback(async () => {
    const peer = composePeerId?.trim();
    const text = composeText.trim();
    if (!peer || !text || composeBusy) {
      return;
    }
    const selected = contacts.find(
      (contact) => normalizeStoryUserId(contact.userId) === normalizeStoryUserId(peer),
    );
    if (selected) {
      rememberMessagingPeerDisplay(peer, {
        label: selected.label,
        avatarSrc: selected.avatarSrc,
      });
    }
    setComposeBusy(true);
    setComposeError(null);
    try {
      const conversation = await createOrGetConversation(peer);
      const enriched: MessagingConversation = {
        ...conversation,
        peerLabel: conversation.peerLabel || selected?.label || null,
        peerAvatarSrc: conversation.peerAvatarSrc || selected?.avatarSrc || null,
      };
      const sent = await sendConversationMessage(enriched.id, text);
      setConversations((prev) => {
        const nextRow: MessagingConversation = {
          ...enriched,
          lastMessage: sent,
          updatedAt: sent.createdAt,
          unreadCount: 0,
        };
        if (prev.some((row) => row.id === enriched.id)) {
          return prev
            .map((row) => (row.id === enriched.id ? nextRow : row))
            .sort((a, b) => b.updatedAt - a.updatedAt);
        }
        return [nextRow, ...prev];
      });
      setComposing(false);
      setComposePeerId(null);
      setComposeText('');
      setActive({
        ...enriched,
        lastMessage: sent,
        updatedAt: sent.createdAt,
        unreadCount: 0,
      });
      setMessages([sent]);
      setDraft('');
    } catch (err) {
      const message =
        err instanceof AssetRequestError ? err.message : t('messages.sendFailed');
      setComposeError(message);
    } finally {
      setComposeBusy(false);
    }
  }, [composePeerId, composeText, composeBusy, contacts, setConversations, t]);

  if (composing) {
    const selected = contacts.find(
      (contact) =>
        composePeerId &&
        normalizeStoryUserId(contact.userId) === normalizeStoryUserId(composePeerId),
    );
    return (
      <div
        className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed, styles.wrap].join(
          ' ',
        )}
        role="region"
        aria-label={t('messages.newTitle')}
      >
        <div className={[pageThemeStyles.panelEmbedded, styles.threadPanel].join(' ')}>
          <header className={styles.threadHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={closeCompose}
              aria-label={t('messages.backToInbox')}
            >
              ←
            </button>
            <div className={styles.peerMeta}>
              <span className={styles.peerName}>{t('messages.newTitle')}</span>
            </div>
          </header>

          <div className={styles.composeBody}>
            <p className={styles.composeHint}>{t('messages.newHint')}</p>

            <label className={styles.composeFieldLabel} htmlFor="messages-compose-filter">
              {t('messages.pickRecipient')}
            </label>
            <input
              id="messages-compose-filter"
              className={styles.composeFilter}
              value={composeFilter}
              onChange={(event) => setComposeFilter(event.target.value)}
              placeholder={t('messages.searchPeople')}
            />

            {filteredContacts.length === 0 ? (
              <p className={styles.status} role="status">
                {t('messages.noContacts')}
              </p>
            ) : (
              <ul className={styles.contactList}>
                {filteredContacts.map((contact) => {
                  const selectedRow =
                    composePeerId != null &&
                    normalizeStoryUserId(contact.userId) ===
                      normalizeStoryUserId(composePeerId);
                  return (
                    <li key={contact.userId}>
                      <button
                        type="button"
                        className={[
                          styles.contactRow,
                          selectedRow ? styles.contactRowSelected : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => {
                          setComposePeerId(contact.userId);
                          rememberMessagingPeerDisplay(contact.userId, {
                            label: contact.label,
                            avatarSrc: contact.avatarSrc,
                          });
                        }}
                      >
                        <ProfileAvatarImage
                          src={contact.avatarSrc}
                          className={styles.inboxAvatar}
                        />
                        <span className={styles.inboxName}>{contact.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {selected ? (
              <p className={styles.composeSelected}>
                {t('messages.messagingWith', { name: selected.label })}
              </p>
            ) : null}

            {composeError ? (
              <p className={styles.error} role="alert">
                {composeError}
              </p>
            ) : null}
          </div>

          <form
            className={styles.composer}
            onSubmit={(event) => {
              event.preventDefault();
              void onComposeSend();
            }}
          >
            <input
              className={styles.composerInput}
              dir={resolveChatTextDir(composeText, language)}
              value={composeText}
              onChange={(event) => setComposeText(event.target.value)}
              placeholder={t('messages.composerPlaceholder')}
              maxLength={4000}
              disabled={composeBusy || !composePeerId}
              aria-label={t('messages.composerPlaceholder')}
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={composeBusy || !composePeerId || !composeText.trim()}
            >
              {t('messages.send')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (active) {
    const peer = resolveMessagingPeerDisplay(active.peerUserId, groups, peerFallback, {
      peerLabel: active.peerLabel,
      peerAvatarSrc: active.peerAvatarSrc,
    });
    return (
      <div
        className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed, styles.wrap].join(
          ' ',
        )}
        role="region"
        aria-label={peer.label}
      >
        <div className={[pageThemeStyles.panelEmbedded, styles.threadPanel].join(' ')}>
          <header className={styles.threadHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={onBackToInbox}
              aria-label={t('messages.backToInbox')}
            >
              ←
            </button>
            <ProfileAvatarImage src={peer.avatarSrc} className={styles.peerAvatar} />
            <div className={styles.peerMeta}>
              <span className={styles.peerName}>{peer.label}</span>
            </div>
          </header>

          <div className={styles.messagesScroll}>
            {loadingThread && messages.length === 0 ? (
              <p className={styles.status} role="status">
                {t('messages.loadingThread')}
              </p>
            ) : null}
            {threadError ? (
              <p className={styles.error} role="alert">
                {threadError}
              </p>
            ) : null}
            {messages.map((message) => {
              const mine =
                currentUserId &&
                message.senderId.trim().toLowerCase() === currentUserId.toLowerCase();
              return (
                <div
                  key={message.id}
                  className={[styles.bubbleRow, mine ? styles.bubbleRowMine : styles.bubbleRowPeer]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div
                    className={[styles.bubble, mine ? styles.bubbleMine : styles.bubblePeer].join(
                      ' ',
                    )}
                  >
                    <p
                      className={styles.bubbleText}
                      dir={resolveChatTextDir(message.text, language)}
                    >
                      {message.text}
                    </p>
                    <span className={styles.bubbleTime}>
                      {formatMessageTime(message.createdAt, language)}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={threadEndRef} />
          </div>

          <form
            className={styles.composer}
            onSubmit={(event) => {
              event.preventDefault();
              void onSend();
            }}
          >
            <input
              className={styles.composerInput}
              dir={resolveChatTextDir(draft, language)}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('messages.composerPlaceholder')}
              maxLength={4000}
              disabled={sending}
              aria-label={t('messages.composerPlaceholder')}
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={sending || !draft.trim()}
            >
              {t('messages.send')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('messages.title')}
    >
      <div
        ref={panelRef}
        className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}
      >
        <div className={styles.inboxHeader}>
          <span id={titleId} className={pageThemeStyles.pageTitle}>
            {t('messages.title')}
          </span>
          <button type="button" className={styles.newButton} onClick={startCompose}>
            {t('messages.new')}
          </button>
        </div>

        {loadingInbox ? (
          <p className={pageThemeStyles.browseEmpty} role="status">
            {t('messages.loading')}
          </p>
        ) : null}

        {inboxError ? (
          <p className={styles.error} role="alert">
            {inboxError}
          </p>
        ) : null}

        {openPeerError ? (
          <p className={styles.error} role="alert">
            {openPeerError}
          </p>
        ) : null}

        {!loadingInbox && !inboxError && conversations.length === 0 ? (
          <div className={styles.emptyBlock}>
            <p className={pageThemeStyles.browseEmpty} role="status">
              {t('messages.empty')}
            </p>
            <button type="button" className={styles.emptyCta} onClick={startCompose}>
              {t('messages.new')}
            </button>
          </div>
        ) : null}

        {unreadConversations.length > 0 ? (
          <section className={styles.inboxSection} aria-labelledby={`${titleId}-unread`}>
            <h2 id={`${titleId}-unread`} className={styles.inboxSectionTitle}>
              {t('messages.unreadSection')}
            </h2>
            <ul className={styles.inboxList}>
              {unreadConversations.map((conversation) => (
                <li key={conversation.id}>
                  <InboxConversationRow
                    conversation={conversation}
                    groups={groups}
                    peerFallback={peerFallback}
                    language={language}
                    t={t}
                    onOpen={() => onOpenThread(conversation)}
                  />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {earlierConversations.length > 0 ? (
          <section className={styles.inboxSection} aria-labelledby={`${titleId}-earlier`}>
            {unreadConversations.length > 0 ? (
              <h2 id={`${titleId}-earlier`} className={styles.inboxSectionTitle}>
                {t('messages.earlierSection')}
              </h2>
            ) : null}
            <ul className={styles.inboxList} aria-labelledby={titleId}>
              {earlierConversations.map((conversation) => (
                <li key={conversation.id}>
                  <InboxConversationRow
                    conversation={conversation}
                    groups={groups}
                    peerFallback={peerFallback}
                    language={language}
                    t={t}
                    onOpen={() => onOpenThread(conversation)}
                  />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}
